import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useListPayStationCardTransactions } from 'src/features/payment/api/useListPayStationCardTransactions';
import { badgeOptionsCardTransaction } from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStationCardTransaction } from 'src/features/payment/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';

const columnHelper = createColumnHelper<PayStationCardTransaction>();

export default function CardTransactionsTable() {
    const { operationId = '' } = useParams();
    const { formatMessage, formatNumber } = useIntl();
    const settingsData = React.useContext(SettingsContext);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'created_at', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters(
        operationId ? { initialQueryFilters: { operation: operationId } } : undefined,
    );
    const { parkingId } = useParking();

    const { data, isError, isLoading } = useListPayStationCardTransactions({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columns = [
        columnHelper.accessor('created_at', {
            id: 'created_at',
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.createdAt),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.type),
                badgeOptions: badgeOptionsCardTransaction,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('transaction_number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.transactionNumber),
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({
                row: {
                    original: { amount, currency },
                },
            }) => {
                if (!amount) return '-';
                return formatNumber(amount / 100, {
                    style: 'currency',
                    currency: currency ?? settingsData.default_currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('currency', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.currency),
            },
        }),
        columnHelper.accessor('created_at', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(commonMessages.time),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
            id: 'created_at_range',
        }),
    ];

    return (
        <DataTable
            columns={columns as any}
            data={data}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            search={search}
            semantics={{
                title: formatMessage(paymentsMessages.cardTransactions),
                entryName: formatMessage(paymentsMessages.cardTransaction).toLowerCase(),
                entryNames: formatMessage(paymentsMessages.cardTransactions).toLowerCase(),
                initialColumnVisibility: {
                    created_at_range: false,
                },
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            enableSearchParams={false}
        />
    );
}
